import { Checkbox, Modal, Row, Col, Table, Alert } from 'antd';
import Column from 'antd/es/table/Column';
import Buttons from 'components/Global/Buttons';
import { useState, useMemo, useEffect } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import './index.scss';

type Props = {
	isOpen: boolean;
	handleCancel: () => void;
	selectedNotification: any;
	handleRoleSubmit: any;
	roleData: any;
	moduleName: string;
};

const RoleAssignModal = (props: Props) => {
	const {
		isOpen,
		handleCancel,
		selectedNotification,
		handleRoleSubmit,
		roleData,
		moduleName,
	} = props;

	const [isLoading, setIsLoading] = useState(false);

	// Check if this module uses dual approval
	const hasDualApproval = selectedNotification.requiresDualApproval;

	// Initialize state
	const [firstApproverRoles, setFirstApproverRoles] = useState<any>([]);
	const [secondApproverRoles, setSecondApproverRoles] = useState<any>([]);

	// ✅ Update roles when modal opens or dual approval toggles
	useEffect(() => {
		if (!isOpen) return;

		if (hasDualApproval) {
			const firstRoles = selectedNotification.firstApproverRoles || [];
			const secondRoles = selectedNotification.secondApproverRoles || [];

			// Check if second approvers are actually configured
			const hasValidSecondApprovers =
				secondRoles.length > 0 &&
				secondRoles.some((role: any) => role?.isChecked === true);

			if (hasValidSecondApprovers) {
				setFirstApproverRoles(firstRoles);
				setSecondApproverRoles(secondRoles);
			} else {
				const availableRoles = roleData || firstRoles;

				setFirstApproverRoles(
					firstRoles.length > 0 ? firstRoles : availableRoles
				);

				const uncheckedRoles = availableRoles.map((role: any) => ({
					...role,
					isChecked: false,
				}));
				setSecondApproverRoles(uncheckedRoles);
			}
		} else {
			setFirstApproverRoles(selectedNotification.roles || roleData || []);
			setSecondApproverRoles([]);
		}
	}, [isOpen, hasDualApproval, selectedNotification, roleData]);

	// ✅ Validation: Check if at least one role is selected in first approver
	const hasFirstApprover = useMemo(() => {
		return (
			firstApproverRoles?.some((role: any) => role?.isChecked) || false
		);
	}, [firstApproverRoles]);

	// ✅ Updated validation: Only first approver is required for dual approval
	const isValidSubmission = hasDualApproval ? hasFirstApprover : true;

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			isSubmit: true,
			className: 'secondary-btn',
			fontSize: '1.6rem',
			minWidth: '90px',
			minHeight: '35px',
			onclick: () => {
				handleCancel();
			},
			disabled: isLoading,
		},
		{
			text: 'Assign Roles',
			isSubmit: true,
			className: 'btn-blue',
			fontSize: '1.6rem',
			minWidth: '120px',
			minHeight: '35px',
			onclick: () => {
				setIsLoading(true);
				setTimeout(() => {
					if (hasDualApproval) {
						handleRoleSubmit(selectedNotification.id, {
							firstApproverRoles,
							secondApproverRoles,
						});
					} else {
						handleRoleSubmit(
							selectedNotification.id,
							firstApproverRoles
						);
					}
					setIsLoading(false);
				}, 500);
			},
			disabled: !isValidSubmission,
			isLoading: isLoading,
		},
	];

	// Handle checkbox change for first approvers
	const handleFirstApproverChange = (id: string, value: boolean) => {
		const updatedFirstApprovers = firstApproverRoles.map((item: any) => {
			if (item.id === id) {
				return { ...item, isChecked: value };
			}
			return item;
		});
		setFirstApproverRoles(updatedFirstApprovers);

		// If dual approval, ensure this role is unchecked in second approvers
		if (hasDualApproval && value) {
			const updatedSecondApprovers = secondApproverRoles.map(
				(item: any) => {
					if (item.id === id) {
						return { ...item, isChecked: false };
					}
					return item;
				}
			);
			setSecondApproverRoles(updatedSecondApprovers);
		}
	};

	// Handle checkbox change for second approvers
	const handleSecondApproverChange = (id: string, value: boolean) => {
		const updatedSecondApprovers = secondApproverRoles.map((item: any) => {
			if (item.id === id) {
				return { ...item, isChecked: value };
			}
			return item;
		});
		setSecondApproverRoles(updatedSecondApprovers);

		// Ensure this role is unchecked in first approvers
		if (value) {
			const updatedFirstApprovers = firstApproverRoles.map(
				(item: any) => {
					if (item.id === id) {
						return { ...item, isChecked: false };
					}
					return item;
				}
			);
			setFirstApproverRoles(updatedFirstApprovers);
		}
	};

	// Check if a role is selected in first approvers
	const isRoleInFirstApprovers = (roleId: string) => {
		return (
			firstApproverRoles?.some(
				(role: any) => role?.id === roleId && role?.isChecked
			) || false
		);
	};

	// Check if a role is selected in second approvers
	const isRoleInSecondApprovers = (roleId: string) => {
		return (
			secondApproverRoles?.some(
				(role: any) => role?.id === roleId && role?.isChecked
			) || false
		);
	};

	if (!hasDualApproval) {
		// Single approval modal
		return (
			<Modal
				open={isOpen}
				closable={false}
				width={400}
				onCancel={handleCancel}
				className="role-assign-modal"
				footer={() => {
					return (
						<div>
							<Buttons buttons={buttons} />
						</div>
					);
				}}
			>
				<div className="role-assign-table">
					<h3
						style={{
							marginBottom: '16px',
							fontSize: '16px',
							fontWeight: 600,
							color: '#584495',
							textAlign: 'center',
						}}
					>
						Select Role for {moduleName} (Required)
					</h3>

					<Table
						dataSource={firstApproverRoles || []}
						pagination={false}
						rowKey={(record) => record?.id || Math.random()}
						loading={false}
					>
						<Column
							title=""
							dataIndex="isChecked"
							className="bg-white"
							key="isChecked"
							width="50px"
							render={(record, data: any) => {
								return (
									<Checkbox
										checked={record}
										onChange={(e: any) =>
											handleFirstApproverChange(
												data.id,
												e.target.checked
											)
										}
									/>
								);
							}}
						/>
						<Column
							title="Role Name"
							dataIndex="roleName"
							key="roleName"
							className="bg-white"
						/>
					</Table>
				</div>
			</Modal>
		);
	}

	// Dual approval modal with side-by-side tables
	return (
		<Modal
			open={isOpen}
			closable={false}
			maskClosable={false}
			width={600}
			onCancel={handleCancel}
			className="role-assign-modal dual-approval-modal"
			rootClassName="assign-role-modal"
			centered
			footer={() => {
				return (
					<div>
						<Buttons buttons={buttons} />
					</div>
				);
			}}
			title={
				<div style={{ fontSize: '16px', fontWeight: 600 }}>
					Double Approval Settings - {moduleName}
				</div>
			}
		>
			<div className="role-assign-table">
				{/* Info Banner */}
				<div
					style={{
						// background: '#f0f0ff',
						border: '1px solid #d6d6f5',
						borderRadius: '4px',
						padding: '12px ',
						marginBottom: '20px',
						display: 'flex',
						gap: '8px',
					}}
				>
					<InfoCircleOutlined
						style={{ color: '#584495', fontSize: '16px' }}
					/>
					<div style={{ fontSize: '14px', color: '#584495' }}>
						<div style={{ marginBottom: '4px' }}>
							<strong>If First Approver is selected:</strong>{' '}
							Admin will be the default Second Approver along with
							any other selected roles.
						</div>
					</div>
				</div>

				<Row gutter={24}>
					{/* First Approver Column */}
					<Col span={12}>
						<div style={{ marginBottom: '12px' }}>
							<h4
								style={{
									fontSize: '14px',
									fontWeight: 500,
									color: '#584495',
									marginBottom: '8px',
								}}
							>
								First Approver
							</h4>

							<Table
								dataSource={firstApproverRoles || []}
								pagination={false}
								rowKey={(record) => record?.id || Math.random()}
								loading={false}
								size="small"
								scroll={{ y: 280 }}
								showHeader={false}
							>
								<Column
									dataIndex="isChecked"
									className="bg-white"
									key="isChecked"
									width="40px"
									render={(record, data: any) => {
										const disabledInSecond =
											isRoleInSecondApprovers(data.id);
										return (
											<Checkbox
												checked={record}
												disabled={disabledInSecond}
												onChange={(e: any) =>
													handleFirstApproverChange(
														data.id,
														e.target.checked
													)
												}
											/>
										);
									}}
								/>
								<Column
									dataIndex="roleName"
									key="roleName"
									className="bg-white"
								/>
							</Table>
						</div>
					</Col>

					{/* Second Approver Column */}
					<Col span={12}>
						<div style={{ marginBottom: '12px' }}>
							<h4
								style={{
									fontSize: '14px',
									fontWeight: 500,
									color: '#584495',
									marginBottom: '8px',
								}}
							>
								Second Approver
							</h4>

							<Table
								dataSource={secondApproverRoles || []}
								pagination={false}
								rowKey={(record) => record?.id || Math.random()}
								loading={false}
								size="small"
								scroll={{ y: 280 }}
								showHeader={false}
							>
								<Column
									dataIndex="isChecked"
									className="bg-white"
									key="isChecked"
									width="40px"
									render={(record, data: any) => {
										const disabledInFirst =
											isRoleInFirstApprovers(data.id);
										return (
											<Checkbox
												checked={record}
												disabled={disabledInFirst}
												onChange={(e: any) =>
													handleSecondApproverChange(
														data.id,
														e.target.checked
													)
												}
											/>
										);
									}}
								/>
								<Column
									dataIndex="roleName"
									key="roleName"
									className="bg-white"
								/>
							</Table>
						</div>
					</Col>
				</Row>
			</div>
		</Modal>
	);
};

export default RoleAssignModal;
